import type { FunctionalComponent } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { env } from '@/lib/env.ts';
import { useNaverSdk } from '@/hooks/use-external-script.ts';

interface Props {
  onFail?: (message: string) => void;
}

/**
 * 네이버 로그인 버튼.
 *
 * 네이버 SDK 는 자기 마크업의 버튼만 클릭 이벤트로 받는다. 그래서 SDK 버튼을
 * 숨겨두고 시안 디자인의 버튼 클릭을 그쪽으로 넘긴다.
 *
 * 로그인은 팝업(/oauth/naver.callback)에서 끝나고, 결과는 postMessage 로
 * 부모창(login 페이지)이 받는다.
 */
export const NaverLoginButton: FunctionalComponent<Props> = ({ onFail }) => {
  const status = useNaverSdk();
  const hostRef = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (status !== 'ready' || initialized.current) return;
    if (!window.naver || !hostRef.current) return;
    if (!env.naver.clientId || !env.naver.callback) return;

    new window.naver.LoginWithNaverId({
      clientId: env.naver.clientId,
      callbackUrl: env.naver.callback,
      isPopup: true,
      callbackHandle: true,
      loginButton: { color: 'green', type: 3, height: 38 },
    }).init();

    initialized.current = true;
  }, [status]);

  function handleClick() {
    if (!env.naver.clientId || !env.naver.callback) {
      onFail?.('네이버 로그인이 설정되지 않았습니다.');
      return;
    }

    if (status === 'error') {
      onFail?.('네이버 로그인 모듈을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
      return;
    }

    const trigger = hostRef.current?.querySelector<HTMLElement>('#naverIdLogin_loginButton');
    if (!trigger) {
      onFail?.('네이버 로그인을 준비하는 중입니다. 잠시 후 다시 시도해 주세요.');
      return;
    }

    trigger.click();
  }

  return (
    <>
      <button type="button" class="login-btn login-btn--naver" onClick={handleClick}>
        <span class="login-btn-icon">
          <img src="/images/naver.png" alt="" />
        </span>
        네이버 로그인
      </button>

      {/* 네이버 SDK 가 실제 버튼을 그리는 자리. 화면에는 보이지 않는다. */}
      <div id="naverIdLogin" ref={hostRef} hidden />
    </>
  );
};
